import { useState, useEffect, useCallback } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { yaml as yamlLang } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import styles from './TransformersTab.module.css'
import Button from './Button'
import Icon from './Icon'
import {
  fetchConnectorActions,
  fetchTransformerVersions,
  createTransformerVersion,
} from '../api/connectors'

// Module-level constants, not recreated per render — CodeMirror reconfigures
// its extensions whenever this array's *reference* changes, so a literal
// passed inline as a prop would churn on every keystroke for no reason.
const YAML_EXTENSIONS = [yamlLang()]

function starterYaml(actionName) {
  return `# Transformer for ${actionName}\n` +
    '# Maps this action\'s raw connector payload into GaussIQ\'s common format.\n' +
    '# Replace this with the real mapping, then save to create version 1.\n'
}

/**
 * TransformersTab — view, edit, and version the YAML transformer that converts
 * a connector action's raw system JSON into GaussIQ's common TIF/KIF shapes
 * (see gaussiq-connectors' `transformers` table and routes/connector.py's
 * get_connector_props, which reads the latest version at runtime).
 *
 * A save never overwrites — it always inserts a new version row, so this is
 * also how an action gets its first transformer (save with none yet and the
 * backend assigns version "1"). Selecting an older version loads its yaml into
 * the editor too, so re-saving it (edited or not) promotes it back to latest —
 * the version list is a full, append-only edit trail, not a single mutable slot.
 *
 * Mounted only while its tab is selected in ConnectorConfigModal, so it always
 * fetches fresh on mount — no visibility prop needed.
 *
 * Props:
 *   connectorId   {string|number}
 *   initialAction {string} — display_name to preselect (mirrors ConnectorConfigModal's
 *                 own initialAction handling for the Actions tab)
 */
export default function TransformersTab({ connectorId, initialAction }) {
  const [actions, setActions] = useState([])
  const [actionsLoading, setActionsLoading] = useState(false)
  const [actionsError, setActionsError] = useState(null)
  const [selectedAction, setSelectedAction] = useState('')

  const [versions, setVersions] = useState([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [versionsError, setVersionsError] = useState(null)
  const [selectedVersionId, setSelectedVersionId] = useState(null)

  const [draft, setDraft] = useState('')
  const [saveStatus, setSaveStatus] = useState('idle') // 'idle' | 'saving' | 'saved' | 'error'
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    if (!connectorId) return
    setActionsLoading(true)
    setActionsError(null)
    fetchConnectorActions(connectorId)
      .then(data => setActions(data.actions ?? []))
      .catch(e => setActionsError(e.message))
      .finally(() => setActionsLoading(false))
  }, [connectorId])

  // Preselect: the pill's display_name if given, else the first action once loaded.
  useEffect(() => {
    if (actions.length === 0 || selectedAction) return
    const match = initialAction && actions.find(a => a.display_name === initialAction)
    setSelectedAction(match?.action_name ?? actions[0].action_name)
  }, [actions, initialAction, selectedAction])

  const loadVersions = useCallback(() => {
    if (!connectorId || !selectedAction) return
    setVersionsLoading(true)
    setVersionsError(null)
    setSaveStatus('idle')
    setSaveError(null)
    fetchTransformerVersions(connectorId, selectedAction)
      .then(data => {
        const list = data.versions ?? []
        setVersions(list)
        setSelectedVersionId(list[0]?.transformer_id ?? null)
        // No transformer yet: seed the editor with a starter template rather than
        // leaving it blank, so Save is enabled right away instead of silently
        // sitting disabled until the user types something into an empty box.
        setDraft(list[0]?.yaml ?? starterYaml(selectedAction))
      })
      .catch(e => setVersionsError(e.message))
      .finally(() => setVersionsLoading(false))
  }, [connectorId, selectedAction])

  useEffect(() => { loadVersions() }, [loadVersions])

  const selectedVersion = versions.find(v => v.transformer_id === selectedVersionId) ?? null
  const hasNoTransformer = !versionsLoading && !versionsError && versions.length === 0

  function handleSelectVersion(id) {
    const v = versions.find(x => x.transformer_id === id)
    if (!v) return
    setSelectedVersionId(id)
    setDraft(v.yaml)
    setSaveStatus('idle')
  }

  function handleDownload(version) {
    const blob = new Blob([version.yaml], { type: 'text/yaml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${selectedAction}_v${version.version ?? version.transformer_id}.yml`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  async function handleSave() {
    if (!draft.trim()) return
    setSaveStatus('saving')
    setSaveError(null)
    try {
      const { transformer } = await createTransformerVersion(connectorId, {
        action_name: selectedAction,
        yaml: draft,
      })
      setVersions(prev => [transformer, ...prev])
      setSelectedVersionId(transformer.transformer_id)
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus(s => s === 'saved' ? 'idle' : s), 1500)
    } catch (e) {
      setSaveError(e.message)
      setSaveStatus('error')
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.filterRow}>
        <label className={styles.filterLabel} htmlFor="ccm-transformer-action">
          Filter by action
        </label>
        <select
          id="ccm-transformer-action"
          className={styles.actionSelect}
          value={selectedAction}
          onChange={e => setSelectedAction(e.target.value)}
          disabled={actionsLoading || actions.length === 0}
        >
          {actions.map(a => (
            <option key={a.action_name} value={a.action_name}>{a.action_name}</option>
          ))}
        </select>
      </div>

      {actionsLoading && <p className={styles.placeholder}>Loading actions…</p>}
      {actionsError && !actionsLoading && (
        <p className={styles.placeholder}>Failed to load actions: {actionsError}</p>
      )}
      {!actionsLoading && !actionsError && actions.length === 0 && (
        <p className={styles.placeholder}>No actions configured for this connector.</p>
      )}

      {!actionsLoading && !actionsError && selectedAction && (
        <div className={styles.body}>
          {/* ── Version history ── */}
          <div className={styles.history}>
            <div className={styles.historyHeader}>Versions</div>
            {versionsLoading && <p className={styles.placeholder}>Loading…</p>}
            {versionsError && !versionsLoading && (
              <p className={styles.placeholder}>Failed to load versions: {versionsError}</p>
            )}
            {hasNoTransformer && (
              <p className={styles.placeholder}>No transformer yet for this action.</p>
            )}
            {!versionsLoading && !versionsError && versions.map((v, i) => (
              <div
                key={v.transformer_id}
                className={`${styles.versionRow} ${v.transformer_id === selectedVersionId ? styles.versionRowActive : ''}`}
              >
                <button className={styles.versionSelectBtn} onClick={() => handleSelectVersion(v.transformer_id)}>
                  <span className={styles.versionLabel}>
                    v{v.version ?? '0'}{i === 0 ? ' · latest' : ''}
                  </span>
                  <span className={styles.versionDate}>
                    {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                  </span>
                </button>
                <button
                  className={styles.downloadBtn}
                  onClick={() => handleDownload(v)}
                  aria-label={`Download version ${v.version ?? v.transformer_id}`}
                  title="Download this version"
                >
                  <Icon name="download" className={styles.downloadIcon} />
                </button>
              </div>
            ))}
          </div>

          {/* ── Editor ── */}
          <div className={styles.editor}>
            <div className={styles.editorHeader}>
              <span className={styles.editorTitle}>
                {hasNoTransformer
                  ? 'New transformer'
                  : `Editing v${selectedVersion?.version ?? '—'}${selectedVersionId === versions[0]?.transformer_id ? ' · latest' : ''}`}
              </span>
              <div className={styles.editorActions}>
                {selectedVersion && (
                  <button className={styles.downloadActionBtn} onClick={() => handleDownload(selectedVersion)}>
                    <Icon name="download" className={styles.downloadActionIcon} /> Download
                  </button>
                )}
                <Button onClick={handleSave} disabled={saveStatus === 'saving' || !draft.trim()}>
                  {saveStatus === 'saving'
                    ? 'Saving…'
                    : saveStatus === 'saved'
                      ? 'Saved ✓'
                      : hasNoTransformer
                        ? 'Create Transformer'
                        : 'Save as New Version'}
                </Button>
              </div>
            </div>

            {saveStatus === 'error' && (
              <p className={styles.saveError}>Failed to save: {saveError}</p>
            )}

            <div className={styles.codeMirrorWrap}>
              <CodeMirror
                value={draft}
                height="360px"
                theme={oneDark}
                extensions={YAML_EXTENSIONS}
                onChange={setDraft}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
